import { Document, Page, StyleSheet, Text, View } from "@react-pdf/renderer";

import {
  PDF_COLORS,
  PDF_COMPANY,
  formatGeneratedAt,
  formatRp,
} from "@/lib/pdf";

import { CASH_METHOD_LABELS, CASH_SOURCE_LABELS } from "../cash-report-labels";
import { formatDate, formatPeriod } from "./format";

import type { CashEntry } from "@artisancode/api-types";

const COMPANY_NAME = PDF_COMPANY.name;
const COMPANY_ADDRESS = PDF_COMPANY.address;

const ORANGE = PDF_COLORS.orange;
const ORANGE_LIGHT = "#FBEAE0";
const DARK = PDF_COLORS.dark;
const GRAY = PDF_COLORS.gray;

const styles = StyleSheet.create({
  page: { padding: 36, fontSize: 9, fontFamily: "Helvetica", color: DARK },
  headerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  docTitle: { fontFamily: "Helvetica-Bold", fontSize: 22, color: ORANGE },
  companyName: { fontFamily: "Helvetica-Bold", fontSize: 13, marginTop: 4 },
  subtitle: { fontSize: 9, color: GRAY, marginTop: 2, maxWidth: "70%" },
  metaBlock: { alignItems: "flex-end" },
  metaLine: { flexDirection: "row", marginTop: 4 },
  metaLabel: { color: GRAY, marginRight: 12 },
  metaValue: { fontFamily: "Helvetica-Bold", fontSize: 10 },
  divider: {
    borderBottomWidth: 2,
    borderBottomColor: ORANGE,
    marginTop: 16,
    marginBottom: 20,
  },
  table: { borderWidth: 1, borderColor: "#eee" },
  tableHeaderRow: { flexDirection: "row", backgroundColor: ORANGE_LIGHT },
  tableHeaderCell: {
    fontFamily: "Helvetica-Bold",
    fontSize: 9,
    padding: 8,
    textAlign: "center",
  },
  tableRow: { flexDirection: "row", borderTopWidth: 1, borderTopColor: "#eee" },
  tableCell: { fontSize: 9, padding: 8, textAlign: "center" },
  tableCellLeft: { textAlign: "left" },
  tableCellRight: { textAlign: "right" },
  summaryBlock: { marginTop: 20, alignItems: "flex-end" },
  summaryRow: {
    flexDirection: "row",
    width: 240,
    justifyContent: "space-between",
    paddingVertical: 3,
  },
  summaryLabel: { color: GRAY },
  summaryValue: { fontFamily: "Helvetica-Bold" },
  grandTotalRow: {
    flexDirection: "row",
    width: 240,
    justifyContent: "space-between",
    paddingVertical: 6,
    marginTop: 4,
    borderTopWidth: 2,
    borderTopColor: ORANGE,
  },
  grandTotalLabel: { fontFamily: "Helvetica-Bold", fontSize: 10 },
  grandTotalValue: {
    fontFamily: "Helvetica-Bold",
    fontSize: 11,
    color: ORANGE,
  },
  generatedAt: {
    position: "absolute",
    bottom: 20,
    left: 36,
    fontSize: 7,
    color: GRAY,
  },
  pageNumber: {
    position: "absolute",
    bottom: 20,
    right: 36,
    fontSize: 7,
    color: GRAY,
  },
});

export function CashReportPdfDocument({
  items,
  totalByMethod,
  grandTotal,
  periodStart,
  periodEnd,
  sourceLabel,
}: {
  items: CashEntry[];
  totalByMethod: Record<string, number>;
  grandTotal: number;
  periodStart: string;
  periodEnd: string;
  sourceLabel: string;
}) {
  const rows = [...items].sort((a, b) => a.date.localeCompare(b.date));
  const generatedAt = formatGeneratedAt();
  const periodLabel = formatPeriod(periodStart, periodEnd);

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.headerRow}>
          <View>
            <Text style={styles.docTitle}>LAPORAN KAS</Text>
            <Text style={styles.companyName}>{COMPANY_NAME}</Text>
            <Text style={styles.subtitle}>{COMPANY_ADDRESS}</Text>
          </View>
          <View style={styles.metaBlock}>
            <View style={styles.metaLine}>
              <Text style={styles.metaLabel}>Periode</Text>
              <Text style={styles.metaValue}>{periodLabel}</Text>
            </View>
            <View style={styles.metaLine}>
              <Text style={styles.metaLabel}>Sumber</Text>
              <Text style={styles.metaValue}>{sourceLabel}</Text>
            </View>
          </View>
        </View>

        <View style={styles.divider} />

        <View style={styles.table}>
          <View style={styles.tableHeaderRow}>
            <Text style={[styles.tableHeaderCell, { width: "14%" }]}>
              Tanggal
            </Text>
            <Text style={[styles.tableHeaderCell, { width: "16%" }]}>
              Sumber
            </Text>
            <Text
              style={[
                styles.tableHeaderCell,
                { width: "34%", textAlign: "left" },
              ]}
            >
              Detail
            </Text>
            <Text style={[styles.tableHeaderCell, { width: "16%" }]}>
              Metode
            </Text>
            <Text style={[styles.tableHeaderCell, { width: "20%" }]}>
              Jumlah
            </Text>
          </View>

          {rows.map((row, i) => (
            <View style={styles.tableRow} key={i} wrap={false}>
              <Text style={[styles.tableCell, { width: "14%" }]}>
                {formatDate(row.date)}
              </Text>
              <Text style={[styles.tableCell, { width: "16%" }]}>
                {CASH_SOURCE_LABELS[row.source]}
              </Text>
              <Text
                style={[
                  styles.tableCell,
                  styles.tableCellLeft,
                  { width: "34%" },
                ]}
              >
                {row.label}
              </Text>
              <Text style={[styles.tableCell, { width: "16%" }]}>
                {CASH_METHOD_LABELS[row.method]}
              </Text>
              <Text
                style={[
                  styles.tableCell,
                  styles.tableCellRight,
                  { width: "20%" },
                ]}
              >
                {formatRp(row.amount)}
              </Text>
            </View>
          ))}
        </View>

        <View style={styles.summaryBlock}>
          {Object.entries(totalByMethod).map(([method, amount]) => (
            <View style={styles.summaryRow} key={method}>
              <Text style={styles.summaryLabel}>
                {CASH_METHOD_LABELS[method as CashEntry["method"]] ?? method}
              </Text>
              <Text style={styles.summaryValue}>{formatRp(amount)}</Text>
            </View>
          ))}
          <View style={styles.grandTotalRow}>
            <Text style={styles.grandTotalLabel}>Total Kas Masuk</Text>
            <Text style={styles.grandTotalValue}>{formatRp(grandTotal)}</Text>
          </View>
        </View>

        <Text style={styles.generatedAt} fixed>
          Dicetak pada {generatedAt}
        </Text>
        <Text
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) =>
            `Halaman ${pageNumber}/${totalPages} - Kas ${periodLabel}`
          }
          fixed
        />
      </Page>
    </Document>
  );
}
